<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { onBeforeUnmount } from "vue"
import { cn } from "@/lib/utils"
import { useSidebar } from "./utils"

const props = withDefaults(defineProps<{
  class?: HTMLAttributes["class"]
  /** Tooltip / accessible name; the rail toggles on click and resizes on drag. */
  label?: string
}>(), {
  label: "Toggle Sidebar",
})

const { toggleSidebar, open, setOpen, width, setWidth, isResizing } = useSidebar()

/** Pointer travel (px) before a press counts as a drag instead of a click. */
const DRAG_THRESHOLD_PX = 3

let startX = 0
let startWidthPx = 0
let side: "left" | "right" = "left"
let pressed = false
let dragged = false
// A drag ends with a click event on the rail; this swallows it so it doesn't toggle.
let suppressClick = false

function remInPx() {
  return Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
}

function onPointerDown(event: PointerEvent) {
  if (event.button !== 0) return
  const el = event.currentTarget as HTMLElement
  side = el.closest("[data-side]")?.getAttribute("data-side") === "right" ? "right" : "left"
  startX = event.clientX
  // Starting from collapsed, the drag opens the sidebar and sizes it from zero.
  startWidthPx = open.value ? width.value * remInPx() : 0
  pressed = true
  dragged = false
  el.setPointerCapture(event.pointerId)
}

function onPointerMove(event: PointerEvent) {
  if (!pressed) return
  const dx = event.clientX - startX
  if (!dragged) {
    if (Math.abs(dx) < DRAG_THRESHOLD_PX) return
    dragged = true
    isResizing.value = true
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    if (!open.value) setOpen(true)
  }
  const widthPx = startWidthPx + (side === "left" ? dx : -dx)
  setWidth(widthPx / remInPx())
}

function endDrag(event?: PointerEvent) {
  if (!pressed) return
  pressed = false
  if (event) (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId)
  if (!dragged) return
  suppressClick = true
  isResizing.value = false
  document.body.style.cursor = ""
  document.body.style.userSelect = ""
  setWidth(width.value, { commit: true })
}

function onClick() {
  if (suppressClick) {
    suppressClick = false
    return
  }
  toggleSidebar()
}

onBeforeUnmount(() => endDrag())
</script>

<template>
  <button
    data-sidebar="rail"
    data-slot="sidebar-rail"
    :aria-label="props.label"
    :tabindex="-1"
    :title="props.label"
    :class="cn(
      'hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 touch-none transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex',
      'in-data-resizing:after:bg-sidebar-border',
      'cursor-col-resize',
      '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
      'hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
      '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
      '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
      props.class,
    )"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="endDrag"
    @pointercancel="endDrag"
    @click="onClick"
  >
    <slot />
  </button>
</template>
